import cl from 'classnames';
import { debounce, noop } from 'lodash';
import * as React from 'react';
import styled, { css } from 'styled-components';

import { figmaColors } from '../colors';
import { colorsV2 } from '../colors-v2';
import renderNode, { Span } from '../renderNode';

export interface ButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
  size?: 'large' | 'medium' | 'small' | 'x-small';
  type?: 'default' | 'primary' | 'secondary' | 'tertiary' | 'supportive' | '2nd-supportive';
  iconLeft?: JSX.Element;
  iconRight?: JSX.Element;
  disabled?: boolean;
  shape?: 'circle';
  style?: React.CSSProperties;
  count?: string;
  onClickDebounce?: (...args: any[]) => void;
  debounceInterval?: number;
  loading?: boolean;
  outline?: boolean;
  isMobile?: boolean;
  children?: any;
}

const primaryStyle = `
  &:hover,
    &.hover {
      background-color: ${figmaColors.primary7};
    }
    &:active,
    &.active {
      background-color: ${figmaColors.primary8};
    }
`;

const StyledAntButton = styled.button<ButtonProps>`
  border-radius: 8px;
  outline: none;
  cursor: pointer;
  border: none;
  font-weight: 600;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  transition-delay: 0s;

  &.size-large {
    font-size: 20px;
    padding: 2px 30px;
  }
  &.size-medium {
    font-size: 14px;
    line-height: 24px;
    padding: 8px 16px;
  }
  &.size-small {
    font-size: 14px;
    padding: 4px 10px;
    border-radius: 4px;
  }
  &.size-x-small {
    font-size: 12px;
    line-height: 24px;
    padding: 2px 8px;
    border-radius: 4px;
  }
  &.type-default {
    background-color: #c4c4c4;
    color: #000;
  }
  &.type-primary {
    color: ${colorsV2.white100};
    background: ${colorsV2.primary100};
    &:disabled,
    &.disabled {
      background-color: ${figmaColors.primary2};
    }
    ${(p) => !p.isMobile && primaryStyle}
  }
  &.type-secondary {
    color: ${figmaColors.neutral5};
    background-color: ${figmaColors.W2B1};
    border: 1px solid ${figmaColors.neutral1};
    &:hover,
    &.hover {
      color: ${figmaColors.primary7};
      border-color: ${figmaColors.primary7};
    }
    &:active,
    &.active {
      color: ${figmaColors.primary8};
      border-color: ${figmaColors.primary8};
    }
    &:disabled,
    &.disabled {
      background-color: ${figmaColors.W2B2};
      border-color: ${figmaColors.neutral1};
      color: ${figmaColors.W2B1};
    }
    ${(p) =>
      p.outline &&
      css`
        background-color: transparent;
        border: 2px solid ${colorsV2.gray20};
        &:hover,
        &.hover {
          background-color: transparent;
          border-color: ${colorsV2.gray40};
        }
        &:active,
        &.active {
          background-color: transparent;
          border-color: ${colorsV2.gray60};
        }
        &:disabled,
        &.disabled {
          background-color: transparent;
          border-color: ${colorsV2.gray40};
          color: ${colorsV2.gray60};
        }
      `}
  }
  &.type-tertiary {
    background-color: transparent;
    color: ${figmaColors.neutral5};
    &:hover,
    &.hover {
      color: ${figmaColors.primary7};
      text-decoration: underline;
    }
    &:active,
    &.active {
      color: ${figmaColors.primary8};
      text-decoration: underline;
    }
    &:disabled,
    &.disabled {
      color: ${figmaColors.neutral2};
    }
  }
  &.type-supportive {
    background-color: ${figmaColors.supportive1};
    color: ${figmaColors.supportive7};

    &:hover,
    &.hover {
      color: ${figmaColors.supportive1};
      background-color: ${figmaColors.supportive7};
    }
    &:active,
    &.active {
      color: ${figmaColors.supportive1};
      background-color: ${figmaColors.supportive8};
    }
    &:disabled,
    &.disabled {
      background-color: ${figmaColors.supportive1};
      color: ${figmaColors.supportive2};
    }
  }
  &.type-2nd-supportive {
    background-color: ${colorsV2.gray10};
    color: ${colorsV2.gray100};

    &:hover,
    &.hover {
      color: ${colorsV2.black100};
      background-color: ${colorsV2.gray20};
    }
    &:active,
    &.active {
      color: ${colorsV2.black100};
      background-color: ${colorsV2.gray20};
    }
    &:disabled,
    &.disabled {
      background-color: ${figmaColors.W2B2};
      color: ${figmaColors.W2B3};
    }
  }

  .row-center {
    display: flex;
    flex-direction: row;
    align-items: center;
  }
  &.shape-circle {
    border-radius: 50%;
    padding: 0;
    &.size-large {
      font-size: 18px;
      width: 40px;
      height: 40px;
    }
    &.size-medium {
      font-size: 14px;
      width: 32px;
      height: 32px;
    }
    &.size-small {
      font-size: 12px;
      width: 24px;
      height: 24px;
    }
    &.size-x-small {
      font-size: 10px;
      width: 16px;
      height: 16px;
    }
    .row-center {
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
    }
  }
  &.has-count {
    color: #d14a4a;
  }
  .count {
    position: absolute;
    top: -10px;
    right: -5px;
    border: 1px solid ${figmaColors.primary5};
    width: 24px;
    height: 24px;
    border-radius: 15px;
    font-size: 10px;
    line-height: 23px;
    color: ${figmaColors.W2B1};
    background: ${figmaColors.primary5};
  }
`;

export const Button = (props: ButtonProps) => {
  const {
    style,
    size = 'medium',
    type = 'default',
    children,
    iconLeft,
    iconRight,
    disabled = false,
    className = '',
    shape = '',
    count = undefined,
    onClick = noop,
    debounceInterval = 0,
    onClickDebounce = noop,
    loading,
    outline = true,
    isMobile = false,
    ...buttonProps
  } = props;

  const onClickDebounceHandle = React.useMemo(
    () => debounce(onClickDebounce, debounceInterval),
    [debounceInterval, onClickDebounce]
  );

  const onClickHandle = React.useCallback(
    (e) => {
      onClickDebounceHandle();
      return onClick(e);
    },
    [onClick, onClickDebounceHandle]
  );

  return (
    <StyledAntButton
      isMobile={isMobile}
      style={{ position: 'relative', ...style }}
      className={cl(`btn size-${size} type-${type} shape-${shape} ${className}`, {
        'has-count': Boolean(count),
        disabled,
      })}
      disabled={loading || disabled}
      onClick={onClickHandle}
      outline={outline}
      {...buttonProps}
    >
      <div className="row-center">
        {iconLeft && renderNode(Span, iconLeft, {})}
        {children}
        {iconRight && renderNode(Span, iconRight, {})}
      </div>
      {Boolean(count) && <div className="count">{count}</div>}
    </StyledAntButton>
  );
};

export const FullWidthButton = styled(Button)`
  text-align: center;
  line-height: 14px;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  width: 100%;
  margin-bottom: 8px;
  min-height: 44px;
  border: 2px solid ${colorsV2.gray20};
  box-sizing: border-box;
  border-radius: 8px;
`;
